import Navbar from '../components/Navbar.jsx';
import Footer from '../components/Footer.jsx';
import PricingCard from '../components/PricingCard.jsx';

// MAPTRIUM pricing (effective 2026-10-18, per owner-approved copy doc
// MAPTRIUM_MINIMAL_WEBSITE_COPY_AND_PRICING_UPDATE_V2.md).
// Desktop: annual named-user subscriptions, same verified core across tiers,
// differentiated by seats + support. Cloud is PREVIEW pending unit-cost
// benchmarks. Defense & Government is custom-quote only; never claim
// DoD-authorized / FedRAMP without evidence.
const TIERS = [
  {
    tier: 'Individual',
    price: 1499,
    priceUnit: 'per year',
    tagline: 'One mapping specialist. Full GIS + LiDAR + AI toolkit on your workstation.',
    badge: 'Desktop · 1 user',
    summary: { users: '1 named user', seats: '1 install', renewal: 'Annual' },
    features: [
      'Verified GIS mapping, LiDAR tools, and included AI detection profiles',
      'Review and correct AI findings in one workflow',
      'Local processing on your workstation',
      'ArcGIS Online publish, DOCX reports, PPTX briefings',
      'Fine-tune supported detection profiles on your own data',
      'Email support',
    ],
    not_included: [
      '3+ users (Team)',
      'ONNX / TensorFlow model import (Enterprise)',
      'DoD / air-gap deployment (Defense & Government)',
    ],
  },
  {
    tier: 'Team',
    price: 2999,
    priceUnit: 'per year',
    tagline: 'For a small team producing weekly maps and briefings together.',
    badge: 'Desktop · 3 users',
    highlight: true,
    summary: { users: '3 named users', seats: '3 installs', renewal: 'Annual' },
    features: [
      'Everything in Individual',
      '3 named users on one subscription',
      'Shared custom detection profiles across the team',
      'Local training runs on your own hardware',
      'Business-hours email support',
    ],
    not_included: [
      '5+ users (Business)',
      'Enterprise support SLA',
    ],
  },
  {
    tier: 'Business',
    price: 4999,
    priceUnit: 'per year',
    tagline: 'For growing GIS departments and 5-person consulting teams.',
    badge: 'Desktop · 5 users',
    summary: { users: '5 named users', seats: '5 installs', renewal: 'Annual' },
    features: [
      'Everything in Team',
      '5 named users',
      'Priority support channel',
      'Onboarding included',
      'Organizational license management',
    ],
    not_included: [
      '6+ users (Enterprise)',
      'ONNX / TensorFlow / Detectron2 model import (Enterprise)',
      'Multi-tenant Hub (Enterprise)',
    ],
  },
  {
    tier: 'Enterprise',
    price: null,
    priceUnit: 'Custom quote',
    tagline: 'For agencies, state offices, and multi-office consultancies (6+ users).',
    badge: 'Desktop · 6+ users',
    summary: { users: '6+ named users', seats: 'Negotiated', renewal: 'Annual' },
    features: [
      'Everything in Business',
      '6+ named users',
      'ONNX / TensorFlow / Detectron2 model import',
      'Multi-tenant Hub for distributed teams',
      '24/7 support SLA',
      'Dedicated onboarding',
    ],
    not_included: [
      'DoD / air-gap deployment (see Defense & Government)',
    ],
  },
  {
    tier: 'Cloud (Preview)',
    price: 99,
    priceUnit: 'from · per month',
    tagline: 'Hosted MAPTRIUM for teams who prefer browser access. Request access while we validate processing allowances.',
    badge: 'Cloud · request access',
    summary: { users: 'Starter 1 · Pro 3 · Business 10', seats: 'Per plan', renewal: 'Monthly' },
    features: [
      'Hosted platform. Nothing to install.',
      'Supported GIS and AI workflows via browser',
      'Starter $99 · Professional $299 · Business $799 · Enterprise custom',
      'LiDAR / AI-intensive jobs subject to disclosed processing allowances',
    ],
    not_included: [
      'Checkout (currently request-access only)',
      'Full feature parity w/ Desktop under validation',
      'Unlimited GPU compute (hosted jobs are metered)',
    ],
  },
  {
    tier: 'Defense & Government',
    price: null,
    priceUnit: 'Custom · request quote',
    tagline: 'On-premises and private-environment deployment, assessed case by case for organizations with specialized security requirements.',
    badge: 'Secured deployments',
    summary: { users: 'Negotiated', seats: 'Hard-locked per install', renewal: 'Annual' },
    features: [
      'On-prem / private-environment deployment patterns',
      'Hard-lock per-install activation enforcement',
      'All Enterprise features included',
      'FedRAMP / FIPS / CUI compliance evaluated per deployment',
      'Procurement-friendly contracting',
      'Tailored demonstration and discovery',
    ],
    not_included: [
      'Published availability claims. Contact our team for a tailored deployment discussion.',
    ],
  },
];

// Comparison matrix for the pricing table (Product × Plan × Price × Users).
// Matches the owner-approved pricing sheet format for maptrium.com.
const PRICING_MATRIX = [
  { product: 'Desktop',  plan: 'Individual',    price: '$1,499/year',  users: '1',          cta: 'Request demo' },
  { product: 'Desktop',  plan: 'Team',          price: '$2,999/year',  users: '3',          cta: 'Get MAPTRIUM' },
  { product: 'Desktop',  plan: 'Business',      price: '$4,999/year',  users: '5',          cta: 'Contact sales' },
  { product: 'Desktop',  plan: 'Enterprise',    price: 'Custom',       users: '6+',         cta: 'Request quote' },
  { product: 'Cloud',    plan: 'Starter',       price: '$99/month',    users: '1',          cta: 'Request access', preview: true },
  { product: 'Cloud',    plan: 'Professional',  price: '$299/month',   users: '3',          cta: 'Request access', preview: true },
  { product: 'Cloud',    plan: 'Business',      price: '$799/month',   users: '10',         cta: 'Contact sales',  preview: true },
  { product: 'Cloud',    plan: 'Enterprise',    price: 'Custom',       users: 'Negotiated', cta: 'Request quote',  preview: true },
  { product: 'Defense & Government', plan: 'Specialized Deployment', price: 'Custom', users: 'Negotiated', cta: 'Contact team' },
];

export default function Landing() {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-b from-navy to-navy-800 text-white">
        <div className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center mb-5 text-gold font-semibold uppercase tracking-[0.22em] text-sm">
              GIS Mapping · LiDAR · AI
            </div>
            <h1 className="text-5xl md:text-6xl font-bold leading-tight font-alata">
              GIS Mapping, LiDAR &amp; AI.<br />
              <span className="text-gold">One Connected Workflow.</span>
            </h1>
            <p className="mt-6 text-lg text-white/85 max-w-xl leading-relaxed">
              Start with built-in AI detection profiles, review and correct what the system finds,
              and refine supported models for your work alongside GIS mapping, LiDAR, and
              professional briefing tools. A clearer path from raw data to reviewed maps and
              briefings, with humans validating important findings.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="https://qmd.atlasgeo.ai/" target="_blank" rel="noopener noreferrer" className="btn btn-gold">
                Try the live demo →
              </a>
              <a href="#pricing" className="btn border border-gold text-gold hover:bg-gold hover:text-navy transition">
                See pricing
              </a>
              <a href="#capabilities" className="btn border border-white/40 text-white hover:bg-white hover:text-navy transition">
                What it does
              </a>
            </div>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm text-white/75 max-w-lg">
              <div>✓ GIS mapping &amp; analysis</div>
              <div>✓ LiDAR &amp; 3D data</div>
              <div>✓ Ready-to-use AI detection</div>
              <div>✓ Review &amp; correct findings</div>
              <div>✓ ArcGIS Online publish</div>
              <div>✓ Maps, reports &amp; briefings</div>
            </div>
          </div>
          <div className="relative">
            <HeroMockup />
          </div>
        </div>
      </section>

      {/* Value strip: the "replace this team" hook */}
      <section aria-labelledby="value-headline" className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid md:grid-cols-[1fr_2fr] gap-8 items-center">
            <div>
              <div className="tag tag-gold mb-3">Why teams choose MAPTRIUM</div>
              <h2 id="value-headline" className="text-3xl font-bold text-navy font-alata leading-tight">
                Replace the salary of a full GIS team with one subscription.
              </h2>
              <p className="mt-3 text-ink-muted text-sm leading-relaxed">
                The average public-sector or commercial GIS build-out runs $250k–$400k a year in salaries alone.
                MAPTRIUM Desktop starts at $1,499/year. One subscription, one connected workflow for
                mapping, LiDAR, and AI-assisted detection.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <RoleCost role="GIS Analyst"        salary="$85k–$110k" note="Data cleanup, geocoding, standard maps" />
              <RoleCost role="Cartographer"       salary="$70k–$95k"  note="Symbology, layout, print-ready output" />
              <RoleCost role="Geospatial Spec."   salary="$95k–$130k" note="Spatial analysis, publishing, QA" />
              <RoleCost role="Report Writer"      salary="$65k–$90k"  note="DOCX briefings, executive summaries" />
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section id="capabilities" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="tag tag-gold mb-3">Capabilities</div>
            <h2 className="text-4xl font-bold text-navy font-alata">
              Everything a GIS team ships. Delivered by MAPTRIUM.
            </h2>
            <p className="mt-3 text-ink-muted max-w-2xl mx-auto">
              GIS mapping, LiDAR processing, AI-assisted detection, and professional briefings,
              all in one workspace, with humans validating important findings.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <Capability
              icon="🗺️"
              title="GIS Mapping & Analysis"
              body="Organize geographic data, create maps, and prepare useful outputs. Ingest CSV, XLSX, KML, KMZ, GeoJSON, Shapefile, GeoPDF. Export PDF, PPTX, DOCX, PNG, GeoJSON, Shapefile, File Geodatabase, and AGOL."
            />
            <Capability
              icon="📡"
              title="LiDAR & 3D Data"
              body="Work with supported point-cloud and three-dimensional geospatial datasets. Standard formats (LAS, LAZ, E57, PLY), orthorectified rasters, and surface models alongside your map outputs."
            />
            <Capability
              icon="🎯"
              title="Ready-to-Use AI Detection"
              body="Choose from included detection profiles to identify supported features. Review and correct results. Fine-tune supported profiles to your needs. Training is a separate, deliberate step the operator initiates."
            />
            <Capability
              icon="🤖"
              title="Ali AI copilot"
              body="Ali builds maps by conversation. Describe the study area, pick a use case, and Ali configures symbology, layout, and narrative. Non-GIS staff produce publication-quality outputs faster."
            />
            <Capability
              icon="🌐"
              title="ArcGIS Online publishing"
              body="One-click publish to your ArcGIS Online organization: hosted Feature Service, WebMap, and shareable URL. Every export honors ESRI item metadata, tags, and thumbnails."
            />
            <Capability
              icon="📊"
              title="Maps, Reports & Briefings"
              body="Turn human-reviewed results into professional maps and briefing materials: PowerPoint decks with slide-per-map layout, DOCX reports with headers and appendices, PDF exhibits, and TAK/ATAK field overlays."
            />
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section id="how" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="tag tag-gold mb-3">Workflow</div>
            <h2 className="text-4xl font-bold text-navy font-alata">
              From raw data to client-ready deliverable in five steps
            </h2>
            <p className="mt-3 text-ink-muted max-w-2xl mx-auto">
              The same workflow whether the output is a briefing deck, an ArcGIS layer, or a printed exhibit.
            </p>
          </div>
          <div className="grid md:grid-cols-5 gap-4">
            {[
              { n: 1, t: 'Onboard',     d: 'Spin up a private workspace in one click. Invite your team.' },
              { n: 2, t: 'Upload',      d: 'Drop CSV, XLSX, KML, KMZ, GeoJSON, Shapefile, GeoPDF, or paste addresses.' },
              { n: 3, t: 'Ask Ali',     d: 'Describe what the map is for. Ali picks symbology, layout, and narrative.' },
              { n: 4, t: 'Publish',     d: 'Push to ArcGIS Online, PowerPoint, DOCX, PDF, and standard GIS exports.' },
              { n: 5, t: 'Deliver',     d: 'Client-branded outputs land in your workspace, audit logged, share links ready.' },
            ].map(s => (
              <div key={s.n} className="card card-pad">
                <div className="w-8 h-8 rounded-full bg-gold text-navy font-bold flex items-center justify-center mb-3">{s.n}</div>
                <div className="font-semibold text-navy mb-1">{s.t}</div>
                <div className="text-sm text-ink-muted leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coming soon strip: desktop + mobile */}
      <section aria-labelledby="coming-soon" className="py-14 bg-gradient-to-br from-navy to-navy-800 text-white">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-[1fr_2fr] gap-8 items-center">
          <div>
            <div className="tag tag-gold mb-3">Coming soon</div>
            <h2 id="coming-soon" className="text-3xl font-bold font-alata leading-tight">
              MAPTRIUM Desktop and Mobile. The same workspace, everywhere you work.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-xl border border-white/15 bg-white/5 p-5">
              <div className="text-3xl mb-2">💻</div>
              <div className="font-semibold text-white mb-1 font-alata">MAPTRIUM Desktop</div>
              <div className="text-sm text-white/75 leading-relaxed">
                Windows-first native app. Works offline, syncs when you're back online. Publishes to
                on-premise ArcGIS Enterprise + local file shares. Ships with air-gapped and connected editions.
              </div>
              <div className="mt-3 inline-flex items-center text-xs uppercase tracking-widest text-gold font-semibold">
                Windows · offline-ready
              </div>
            </div>
            <div className="rounded-xl border border-white/15 bg-white/5 p-5">
              <div className="text-3xl mb-2">📱</div>
              <div className="font-semibold text-white mb-1 font-alata">MAPTRIUM Mobile</div>
              <div className="text-sm text-white/75 leading-relaxed">
                iOS and Android field companion. Capture points, upload, preview maps, share briefings from
                the site visit. Full workspace access on the road.
              </div>
              <div className="mt-3 inline-flex items-center text-xs uppercase tracking-widest text-gold font-semibold">
                iOS · Android · field-ready
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who uses it */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="tag tag-gold mb-3">Built for</div>
            <h2 className="text-4xl font-bold text-navy font-alata">
              Government and commercial teams that need maps fast, branded, and audit-ready.
            </h2>
            <p className="mt-3 text-ink-muted max-w-2xl mx-auto">
              MAPTRIUM works for the analyst who needs one deliverable this afternoon and for the department that
              ships hundreds of maps a month.
            </p>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
            <Audience t="State and local government" d="DOTs, planning departments, public works, and municipal grant offices. From SS4A applications to permit corridor packages." />
            <Audience t="Federal and prime contractors" d="Multi-jurisdictional workspaces, SDVOSB set-aside vehicles, white-label deliverables, SLA-backed support." />
            <Audience t="Emergency management + EOC" d="Incident-command briefings, TAK/ATAK field overlays, GRG-to-PPTX packs, exercises, after-action reports." />
            <Audience t="Engineering + environmental consultancies" d="Client-ready DOCX and PPTX briefings, right-of-way and parcel exhibits, permit tracking maps." />
            <Audience t="Real estate + site selection" d="Market maps, competitor overlays, demographic exhibits, and shareable ArcGIS Online layers for pitches." />
            <Audience t="Insurance + risk analytics" d="Peril and exposure maps, portfolio geocoding, catastrophe-scenario overlays with defensible cartography." />
            <Audience t="Utilities + energy" d="Asset inventories, corridor studies, outage exhibits, and stakeholder briefings across service territories." />
            <Audience t="Non-profits + grant writers" d="Federal grant-grade DOCX packages, before/after impact maps, and single-map ($29) one-off exhibits." />
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="tag tag-gold mb-3">Pricing</div>
            <h2 className="text-4xl font-bold text-navy font-alata">
              One Platform. Flexible Ways to Work.
            </h2>
            <p className="mt-3 text-ink-muted max-w-3xl mx-auto">
              Choose Desktop for local workstation processing, Cloud for hosted access, or talk with
              us about a tailored enterprise deployment. MAPTRIUM brings supported mapping, LiDAR,
              and AI workflows together without requiring separate Classic and LiDAR licenses.
              Included detection profiles and supported correction tools are part of MAPTRIUM,
              not a separate "AI add-on."
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-7xl mx-auto">
            {TIERS.map(t => <PricingCard key={t.tier} {...t} />)}
          </div>

          {/* Clean comparison matrix. Owner-approved 2026-10-18. */}
          <div className="mt-12 max-w-6xl mx-auto bg-white rounded-xl border border-slate-200 shadow-soft overflow-hidden">
            <div className="p-6 border-b border-slate-200">
              <div className="tag tag-gold mb-2">At a glance</div>
              <h3 className="text-2xl font-bold text-navy font-alata">Plan comparison</h3>
              <p className="text-sm text-ink-muted mt-1">
                All plans include the full MAPTRIUM workflow: LiDAR, drone photogrammetry, AI detection,
                Classic CSV mapping, 11-step Sentinel QA, and briefing exports. Every install is hard-locked
                and requires activation.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left">
                    <th className="py-3 px-4 text-xs uppercase tracking-widest text-ink-muted font-semibold">Product</th>
                    <th className="py-3 px-4 text-xs uppercase tracking-widest text-ink-muted font-semibold">Plan</th>
                    <th className="py-3 px-4 text-xs uppercase tracking-widest text-ink-muted font-semibold">Price</th>
                    <th className="py-3 px-4 text-xs uppercase tracking-widest text-ink-muted font-semibold">Users</th>
                    <th className="py-3 px-4 text-xs uppercase tracking-widest text-ink-muted font-semibold text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {PRICING_MATRIX.map((row, i) => (
                    <tr key={i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition">
                      <td className="py-3 px-4 font-semibold text-navy">
                        {row.product}
                        {row.preview && <span className="ml-2 text-[10px] uppercase tracking-widest text-gold font-bold">Preview</span>}
                      </td>
                      <td className="py-3 px-4 text-ink">{row.plan}</td>
                      <td className="py-3 px-4 font-semibold text-navy">{row.price}</td>
                      <td className="py-3 px-4 text-ink-muted">{row.users}</td>
                      <td className="py-3 px-4 text-right">
                        <a href="mailto:info@atlasgeo.ai" className="text-navy hover:text-gold font-semibold text-xs uppercase tracking-widest">
                          {row.cta} →
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-4 bg-slate-50 text-xs text-ink-muted border-t border-slate-200">
              Cloud tiers are in <strong>preview</strong>. Pricing is indicative; final allowances are confirmed on quote.
              Defense &amp; Government requires contracting review. Annual billing is available on all Desktop plans.
            </div>
          </div>

          <div className="text-center mt-8 text-sm text-ink-muted max-w-3xl mx-auto">
            All plans include secure delivery, high-accuracy geocoding, 11-step Sentinel QA validation,
            and the full briefing-export suite. Annual billing available. Federal, state, and SDVOSB set-aside
            procurement vehicles accepted.
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Capability({ icon, title, body }) {
  return (
    <div className="card card-pad">
      <div className="text-gold text-3xl mb-2" aria-hidden="true">{icon}</div>
      <h3 className="font-semibold text-navy mb-2 font-alata">{title}</h3>
      <p className="text-sm text-ink-muted leading-relaxed">{body}</p>
    </div>
  );
}

function Audience({ t, d }) {
  return (
    <div className="card card-pad border-l-4 border-navy">
      <h3 className="font-semibold text-navy mb-2 font-alata">{t}</h3>
      <p className="text-sm text-ink-muted leading-relaxed">{d}</p>
    </div>
  );
}

/**
 * Small cost card used in the "Replace a full GIS team" value strip below
 * the hero. Highlights the role a MAPTRIUM subscription substitutes for.
 */
function RoleCost({ role, salary, note }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3">
      <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold">{role}</div>
      <div className="text-lg font-bold text-navy mt-1 font-alata">{salary}</div>
      <div className="text-[11px] text-ink-muted mt-1 leading-snug">{note}</div>
    </div>
  );
}

function HeroMockup() {
  return (
    <div className="relative">
      <div className="absolute inset-0 bg-gold/20 rounded-2xl blur-3xl" />
      <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden border border-white/10">
        <div className="bg-navy-800 px-4 py-2 flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
          <div className="ml-3 text-white/60 text-xs">maptrium.com</div>
        </div>
        <div className="p-4 bg-slate-50">
          <div className="flex gap-2 text-xs mb-3 flex-wrap">
            <div className="tag tag-green">🤖 Ali</div>
            <div className="tag tag-green">✓ AGOL</div>
            <div className="tag tag-amber">⏳ QA</div>
            <div className="tag tag-navy">PPTX</div>
            <div className="tag tag-navy">DOCX</div>
            <div className="tag tag-navy">PDF</div>
          </div>
          <div className="bg-white rounded-lg p-3 text-xs">
            <div className="flex justify-between mb-2">
              <span className="font-semibold text-navy">Sentinel QA verdict</span>
              <span className="tag tag-green">PASS</span>
            </div>
            <div className="space-y-1 text-ink-muted">
              <div className="flex justify-between"><span>Geocode hit rate</span><span className="text-emerald-600 font-mono">98.4%</span></div>
              <div className="flex justify-between"><span>Bbox inside AO</span><span className="text-emerald-600">✓</span></div>
              <div className="flex justify-between"><span>CRS supported</span><span className="text-emerald-600">EPSG:4326</span></div>
              <div className="flex justify-between"><span>AGOL Feature Service</span><span className="text-emerald-600">published</span></div>
              <div className="flex justify-between"><span>Briefing slides built</span><span className="font-mono">14 of 14</span></div>
            </div>
          </div>
          <div className="grid grid-cols-6 gap-1.5 mt-3">
            {['PPTX', 'DOCX', 'AGOL', 'PDF', 'SHP', 'KMZ'].map(f => (
              <div key={f} className="bg-navy text-white text-[10px] font-bold text-center py-1.5 rounded">{f}</div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
